import Serializable from "@/core/misc/mixins/Serializable";
import Core         from "@/core/Core";
import Sum          from "@/core/types/Sum";
import Decimal      from "decimal.js";

/**
 * Статус оплаты позиции заказа (value object)
 *
 * Все суммы — Sum, в валюте сделки.
 */
class OrderPositionPaymentStatus extends Serializable {
    /** Вся сумма которую нужно оплатить (orderPosition.sumFullSeller) */
    public total: Sum;
    /** Уже оплаченная сумма */
    public payed: Sum;
    /** Сумма заявок на платёж которые ещё предстоит оплатить */
    public inQueue: Sum;
    /** Сумма по которой ещё нет заявок на платёж (total - paymentRequestsSumSeller) */
    public woPaymentRequests: Sum;

    /** Сумма по позиции в текущем документе (фронтовое поле) */
    private _current: Sum | null;
    /** Максимально возможная сумма для текущего документа */
    public maxAvailableCurrent: Sum;

    /** Данные прогресс-бара с сервера (сырые числа для отрисовки).
     *
     *  yellow — вклад текущего документа, и он бывает пустым: у отменённого
     *  платежа сервер его в агрегаты не включает. Остальные поля приходят
     *  всегда. Сам progressBar есть только после гидрации с сервера —
     *  fromOrderPosition его не заполняет. */
    public progressBar: { green: number; blue: number; yellow: number | null };

    static fromServer(data: any, result = new OrderPositionPaymentStatus()): OrderPositionPaymentStatus {
        super.fromServer(data, result);

        // ---- Суммы (super кладёт сырые строки — гидрируем в Sum) ----
        result.total             = Core.utils.hydrate(data.total, Sum.fromServer)!;
        result.payed             = Core.utils.hydrate(data.payed, Sum.fromServer)!;
        result.inQueue           = Core.utils.hydrate(data.inQueue, Sum.fromServer)!;
        result.woPaymentRequests = Core.utils.hydrate(data.woPaymentRequests, Sum.fromServer)!;

        /** maxAvailableCurrent считается и здесь, а не только в
         *  fromOrderPosition: поле объявлено non-nullable, но после гидрации
         *  с сервера оставалось undefined, и потребителям приходилось считать
         *  ту же разность вручную. */
        result.calculateMaxAvailableCurrent();

        return result;
    }

    /**
     * Создаёт статус оплаты из позиции отгрузки
     */
    static fromShipmentPosition(shipmentPosition: any): OrderPositionPaymentStatus {
        return this.fromOrderPosition(shipmentPosition.orderPosition);
    }

    /**
     * Создаёт статус оплаты из позиции заказа
     */
    static fromOrderPosition(orderPosition: any): OrderPositionPaymentStatus {
        const result = new OrderPositionPaymentStatus();
        const paymentStatus = orderPosition.paymentStatus;

        result.payed = paymentStatus.payed;
        result.inQueue = paymentStatus.inQueue;
        result.total = orderPosition.sumFullSeller;
        result.woPaymentRequests = paymentStatus.woPaymentRequests;

        result.calculateMaxAvailableCurrent();
        return result;
    }

    /**
     * Сумма, по которой платёж уже определён: оплачено плюс стоящее в очереди.
     * Дополнение к maxAvailableCurrent относительно total.
     */
    get classified(): Sum {
        return this.payed.plus(this.inQueue);
    }

    /**
     * Позиция полностью классифицирована — свободной суммы не осталось.
     * Отдельный признак, чтобы потребители не сравнивали суммы сами.
     */
    get isFullyClassified(): boolean {
        return this.maxAvailableCurrent.isZero();
    }

    get current(): Sum | null {
        return this._current;
    }

    set current(value: Sum | null) {
        if (value && value.compare(this.maxAvailableCurrent) === 1) {
            this._current = this.maxAvailableCurrent;
        } else {
            this._current = value;
        }
    }

    /**
     * Исключает текущий платёж из агрегатов статуса. Сервер считает
     * payed/inQueue по всем платежам позиции, включая тот, в контексте
     * которого статус запрошен (его сумма — progressBar.yellow). Без
     * вычитания maxAvailableCurrent учёл бы эту сумму дважды: и в
     * агрегате, и в current.
     */
    excludePayment(isPayed: boolean): void {
        const contribution = this.paymentContribution;

        if (contribution === null) {
            /** Вклада в агрегат нет — вычитать нечего, и доли в прогресс-баре
             *  у документа тоже нет: current остаётся пустым, жёлтого сегмента
             *  не будет. Так приходит отменённый платёж: сервер его в payed и
             *  inQueue не считает, и yellow остаётся пустым. Прежний код этого
             *  не ждал и падал на конструкторе Decimal, а с ним не открывалась
             *  вся карточка. */
            this.current = null;
        }
        else {
            if (isPayed === true) {
                this.payed = this.payed.minus(contribution);
            }
            else {
                this.inQueue = this.inQueue.minus(contribution);
            }

            this.calculateMaxAvailableCurrent();
            this.current = contribution;
        }
    }

    /**
     * Вклад текущего документа в агрегаты статуса, либо null, если его там нет.
     *
     * progressBar — сырые данные для отрисовки: типизирован числами, но с
     * сервера приходит строками. Decimal принимает и то, и другое, дальше
     * округление до scale вшито в конструктор Sum. А вот пустое значение он
     * не принимает — на нём и бросает.
     *
     * Самого progressBar может не быть вовсе: fromOrderPosition его не
     * заполняет, поэтому обращение через опциональную цепочку.
     */
    private get paymentContribution(): Sum | null {
        if (Core.utils.isNullOrUndefined(this.progressBar?.yellow) === true) {
            return null;
        }
        else {
            return Sum.fromOperation(new Decimal(this.progressBar.yellow));
        }
    }

    calculateMaxAvailableCurrent(): void {
        this.maxAvailableCurrent = this.total.minus(this.inQueue).minus(this.payed);

        if (this.maxAvailableCurrent.compare(Sum.zero()) === -1) {
            this.maxAvailableCurrent = Sum.zero();
        }
    }
}

export default OrderPositionPaymentStatus;